import { useContext } from 'react';

import { Box, Button, Form, FormActions, Heading, Paragraph, Tag, Text, TextButton } from '@zillow/constellation-10';
import { IconAwardRibbonFilled } from '@zillow/constellation-icons';

import actions from '../../../containers/CreateListingPage/actions';
import { AppSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';
import ModularListingCSEvents from '../../../model/ClickstreamEvent/ModularListingCSEvents';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';

import { StyledContentContainer, StyledDesktopContainer, StyledHeaderContainer, StyledMobileGrid } from './styles';

import { FreemiumContext } from '../../Freemium/context';
import RestrictedListingBanner from '../../RestrictedListingBanner';
import TransitionWrapper from '../../TransitionWrapper';

import type React from 'react';
import type { HeadingPropsInterface } from '@zillow/constellation-10';

export type PageWizardProps = {
  children: React.ReactNode;
  description?: React.ReactNode;
  isTransitioning?: boolean;
  wizardTitle?: React.ReactNode;
  wizardTitleLevel?: HeadingPropsInterface['level'];
  subtitle?: React.ReactNode;
  title?: string;
  titleLevel?: HeadingPropsInterface['level'];
  progressStepper: React.ReactNode;
  totalSteps?: number | null;
  currentStepIndex?: number;
  currentStepKey?: string;
  isBackDisabled?: boolean;
  isNextDisabled?: boolean;
  isNextLoading?: boolean;
  exitCTALabel?: string;
  hideDesktopWizardTitle?: boolean;
  hideExitCTA?: boolean;
  nextButtonType?: string;
  nextLabel?: string;
  secondaryNextLabel?: string;
  backLabel?: string;
  onBackClick?: () => void;
  onNextClick?: () => void;
  onSecondaryNextClick?: () => void;
  onNextDataEventId?: string;
  onExitTriggered?: (...args: Array<unknown>) => void;
  showDesktopViewBreakpoint?: 'md' | 'xl' | 'xxl';
  hideBackButton?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  hideStepIndex?: boolean;
  footerContent?: React.ReactNode;
  isNarrowLayout?: boolean;
  displayPremiumTopOfFunnelHeader?: boolean;
  footer?: React.ReactNode;
};

/** Equal-width columns in the footer row (flex lives on the wrapper, not the buttons — avoids uneven splits). */
const pageWizardFooterSplitColumnCss = {
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: '0%',
  minWidth: 0,
  display: 'flex' as const,
  alignItems: 'center' as const,
};

/** Buttons only stretch within their column; outlined vs filled still differs slightly at the stroke (border). */
const pageWizardFooterNavButtonCss = {
  width: { base: '100%', lg: 'auto' },
  minWidth: 0,
  lg: {
    flexGrow: 0,
    flexBasis: 'auto',
    width: 'auto',
    maxWidth: '176px',
  },
};

const PageWizard = ({
  children,
  description = '',
  isTransitioning = false,
  wizardTitle,
  wizardTitleLevel = 1,
  subtitle = null,
  title = '',
  titleLevel = 2,
  progressStepper,
  totalSteps = null,
  currentStepIndex,
  isBackDisabled = false,
  isNextDisabled = false,
  isNextLoading = false,
  nextButtonType = 'primary',
  nextLabel = 'Next',
  secondaryNextLabel = '',
  backLabel = 'Back',
  exitCTALabel = 'Exit',
  hideDesktopWizardTitle = false,
  hideExitCTA = false,
  onBackClick = () => {},
  onNextClick = () => {},
  onSecondaryNextClick,
  onNextDataEventId,
  onExitTriggered = () => {},
  showDesktopViewBreakpoint = 'md',
  hideBackButton = false,
  hideHeader = false,
  hideFooter = false,
  hideStepIndex = false,
  footerContent = null,
  isNarrowLayout = false,
  displayPremiumTopOfFunnelHeader = false,
  footer = null,
}: PageWizardProps) => {
  const showSecondaryNext = secondaryNextLabel.trim().length > 0;

  const isScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));
  const {
    properties: {
      currentProperty,
      currentProperty: {
        freemium: {
          activeProduct: { type: activeProductType },
        },
      },
    },
    user,
  } = useSelector((state) => state);

  const { freemiumState } = useContext(FreemiumContext);
  const { canAccessTopOfFunnelPremium } = freemiumState;
  const { reducerDispatch } = useContext(ModularListingContext);

  const displayPremiumPurchaseModal = (isOpen: boolean) => {
    actions.updateIsPremiumPurchaseModalOpen({
      reducerDispatch,
      isPremiumPurchaseModalOpen: isOpen,
    });

    ModularListingCSEvents.ClickUpgradeToPremiumFromHeader({
      currentProperty,
      user,
    });
  };

  const renderPremiumTopOfFunnelHeader = () => {
    return (
      <Box>
        <StyledHeaderContainer showDesktopViewBreakpoint={showDesktopViewBreakpoint}>
          <Box css={{ display: { base: 'inline', md: 'flex' }, alignItems: 'center' }}>
            {activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM && (
              <Tag
                css={{
                  backgroundColor: 'bg.softest',
                  color: 'text.accent.brand.hero',
                  mdDown: { position: 'absolute' },
                  marginRight: '200',
                }}
                icon={<IconAwardRibbonFilled />}
              >
                Premium
              </Tag>
            )}
            <Box
              css={{
                visibility:
                  hideDesktopWizardTitle || (isScreenMobile && activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM)
                    ? 'hidden'
                    : 'visible',
              }}
            >
              <Heading
                level={wizardTitleLevel}
                textStyle="heading-xs"
                css={{ marginRight: '400' }}
                data-testid="desktop-wizard-title"
              >
                {wizardTitle}
              </Heading>
              {!hideStepIndex && currentStepIndex && totalSteps && totalSteps > 1 && (
                <Text css={{ display: { mdDown: 'none' } }} asChild>
                  <span>{`Step ${currentStepIndex} of ${totalSteps}`}</span>
                </Text>
              )}
            </Box>
          </Box>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            {wizardTitle !== 'Publish' && activeProductType !== PRODUCT_TYPE_NAME_TO_ID.PREMIUM && (
              <TextButton
                onClick={() => displayPremiumPurchaseModal(true)}
                css={{ marginRight: '800', display: { base: 'none', lg: 'inherit' } }}
                data-eventid={ModularListingCSEvents.ClickUpgradeToPremiumFromHeader.data_event_id}
                icon={<IconAwardRibbonFilled />}
                iconPosition="end"
              >
                Upgrade listing to premium
              </TextButton>
            )}
            <Box css={{ visibility: hideExitCTA ? 'hidden' : 'visible' }}>
              <Button
                tone="neutral"
                emphasis="outlined"
                type="button"
                onClick={onExitTriggered}
                data-testid="desktop-exit-button"
              >
                {exitCTALabel}
              </Button>
            </Box>
          </Box>
        </StyledHeaderContainer>
        {progressStepper && (
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', alignItems: 'center' }}>
            <StyledMobileGrid showDesktopViewBreakpoint={showDesktopViewBreakpoint}>{progressStepper}</StyledMobileGrid>
          </Box>
        )}
      </Box>
    );
  };

  const renderHeader = () => {
    if (canAccessTopOfFunnelPremium && displayPremiumTopOfFunnelHeader) {
      return renderPremiumTopOfFunnelHeader();
    }

    return (
      <Box>
        <StyledHeaderContainer showDesktopViewBreakpoint={showDesktopViewBreakpoint}>
          <Box css={{ visibility: hideDesktopWizardTitle ? 'hidden' : 'visible' }}>
            <Heading
              level={wizardTitleLevel}
              textStyle="heading-xs"
              css={{ marginRight: '400' }}
              data-testid="desktop-wizard-title"
            >
              {wizardTitle}
            </Heading>
            {!hideStepIndex && currentStepIndex && totalSteps && totalSteps > 1 && (
              <Text css={{ display: { mdDown: 'none' } }} asChild>
                <span>{`Step ${currentStepIndex} of ${totalSteps}`}</span>
              </Text>
            )}
          </Box>
          <Box css={{ visibility: hideExitCTA ? 'hidden' : 'visible' }}>
            <Button
              tone="neutral"
              emphasis="outlined"
              type="button"
              onClick={onExitTriggered}
              data-testid="desktop-exit-button"
            >
              {exitCTALabel}
            </Button>
          </Box>
        </StyledHeaderContainer>
        {progressStepper && (
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', alignItems: 'center' }}>
            <StyledMobileGrid showDesktopViewBreakpoint={showDesktopViewBreakpoint}>{progressStepper}</StyledMobileGrid>
          </Box>
        )}
      </Box>
    );
  };

  const renderContent = () => (
    <>
      {title && (
        <Heading
          level={titleLevel}
          data-testid="title"
          css={{
            textStyle: { base: 'heading-xs', lg: 'heading-md' },
            marginBottom: '400',
          }}
        >
          {title}
        </Heading>
      )}
      {subtitle && (
        <Paragraph textStyle="body" data-testid="subtitle" css={{ marginBottom: '400' }}>
          {subtitle}
        </Paragraph>
      )}
      {description && (
        <Paragraph textStyle="body" data-testid="description">
          {description}
        </Paragraph>
      )}
      {children}
    </>
  );

  const renderFooterNextButton = () => (
    <Button
      tone={nextButtonType === 'primary' ? 'brand' : 'neutral'}
      emphasis={nextButtonType === 'primary' ? 'filled' : 'outlined'}
      fluid={true}
      onClick={onNextClick}
      disabled={isNextDisabled}
      data-testid="next-button"
      type="submit"
      loading={isNextLoading}
      data-eventid={onNextDataEventId}
      css={{
        ...pageWizardFooterNavButtonCss,
        ...(showSecondaryNext
          ? {
              lg: {
                flexGrow: 0,
                flexBasis: 'auto',
                width: 'auto',
                maxWidth: 'none',
              },
            }
          : {}),
      }}
    >
      {nextLabel}
    </Button>
  );

  const renderFooter = () => {
    if (footer) {
      return footer;
    }

    return (
      <FormActions
        data-testid="PageWizard-Footer"
        css={{
          marginBlockStart: '0px !important',
          lgDown: {
            borderTop: '1px solid {colors.border.subtle}',
            borderBottom: '1px solid {colors.border.subtle}',
            position: 'fixed',
            width: '100%',
            bottom: 0,
            background: 'white',
            zIndex: '1',
          },
        }}
      >
        {footerContent && (
          <Box
            css={{
              display: { base: 'block', lg: 'none' },
              marginInline: { base: '400', md: '600' },
              marginTop: { base: '400', md: '600' },
            }}
          >
            {footerContent}
          </Box>
        )}
        <Box
          data-testid="PageWizard-SecondaryNextAndNextButtonsContainer"
          css={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: hideBackButton ? '0' : '400',
            justifyContent: hideBackButton ? 'flex-end' : 'flex-start',
            marginBlock: { base: '400', md: '600' },
            marginInline: { base: '400', md: '600', xl: '104px' },
            minWidth: 0,
          }}
        >
          {!hideBackButton && (
            <Box
              css={{
                ...pageWizardFooterSplitColumnCss,
                lg: {
                  flexGrow: 0,
                  flexBasis: 'auto',
                  width: 'auto',
                },
              }}
            >
              <Button
                data-testid="back-button"
                fluid={true}
                disabled={isBackDisabled}
                onClick={onBackClick}
                type="button"
                tone="brand"
                emphasis="outlined"
                css={{
                  ...pageWizardFooterNavButtonCss,
                  lg: {
                    ...pageWizardFooterNavButtonCss.lg,
                    maxWidth: '160px',
                  },
                }}
              >
                {backLabel}
              </Button>
            </Box>
          )}
          {footerContent && (
            <Box
              css={{
                display: { base: 'none', lg: 'flex' },
                flexGrow: 1,
                overflow: 'hidden',
                minWidth: 0,
              }}
            >
              {footerContent}
            </Box>
          )}
          <Box
            data-testid="PageWizard-SecondaryNextAndNextButtons"
            css={{
              ...pageWizardFooterSplitColumnCss,
              gap: '400',
              ...(showSecondaryNext
                ? {
                    lg: {
                      marginLeft: 'auto',
                      flexGrow: 0,
                      flexBasis: 'auto',
                      width: 'auto',
                      maxWidth: '100%',
                    },
                  }
                : {
                    lg: {
                      marginLeft: hideBackButton ? 0 : 'auto',
                      flexGrow: 0,
                      flexBasis: 'auto',
                      width: 'auto',
                    },
                  }),
            }}
          >
            {showSecondaryNext ? (
              <>
                <Box
                  css={{
                    ...pageWizardFooterSplitColumnCss,
                    justifyContent: 'center',
                    lg: {
                      flexGrow: 0,
                      flexBasis: 'auto',
                      width: 'auto',
                    },
                  }}
                >
                  <TextButton
                    type="button"
                    onClick={() => {
                      onSecondaryNextClick?.();
                    }}
                    data-testid="secondary-next-button"
                    css={{
                      width: { base: '100%', lg: 'auto' },
                      textAlign: { base: 'center', lg: 'inherit' },
                    }}
                  >
                    {secondaryNextLabel}
                  </TextButton>
                </Box>
                <Box
                  css={{
                    ...pageWizardFooterSplitColumnCss,
                    lg: {
                      flexGrow: 0,
                      flexBasis: 'auto',
                      width: 'auto',
                    },
                  }}
                >
                  {renderFooterNextButton()}
                </Box>
              </>
            ) : (
              renderFooterNextButton()
            )}
          </Box>
        </Box>
      </FormActions>
    );
  };

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        width: '100%',
        md: { height: '100vh' },
      }}
    >
      <RestrictedListingBanner />
      {!hideHeader && renderHeader()}
      {isTransitioning ? (
        <TransitionWrapper isTransitioning={true} />
      ) : (
        <Form
          asChild={true}
          onSubmit={(e: React.FormEvent) => e.preventDefault()}
          css={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}
        >
          <div>
            <StyledContentContainer hideFooter={hideFooter}>
              {progressStepper && (
                <StyledDesktopContainer showDesktopViewBreakpoint={showDesktopViewBreakpoint}>
                  {progressStepper}
                </StyledDesktopContainer>
              )}
              <Box
                css={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(12, 1fr)',
                  marginTop: { base: '800', xxl: '1200' },
                  marginBottom: { base: '80px' },
                }}
                className="content-grid"
              >
                <Box
                  className="grid-col-1"
                  css={{
                    gridColumnEnd: {
                      base: 'span 0',
                      lg: isNarrowLayout ? 'span 2' : 'span 1',
                    },
                  }}
                />
                <Box
                  className="grid-col-2"
                  css={{
                    gridColumnEnd: {
                      base: 'span 12',
                      lg: isNarrowLayout ? 'span 8' : 'span 10',
                    },
                    justifySelf: 'inherit',
                  }}
                >
                  {renderContent()}
                </Box>
                <Box
                  className="grid-col-3"
                  css={{
                    gridColumnEnd: {
                      base: 'span 0',
                      lg: isNarrowLayout ? 'span 2' : 'span 1',
                    },
                  }}
                />
              </Box>
            </StyledContentContainer>
            {!hideFooter && renderFooter()}
          </div>
        </Form>
      )}
    </Box>
  );
};

export default PageWizard;
